<script setup lang="ts">
import type { Item } from '@/components/fields/KSelectField.vue';
import type { StopTimeObject } from '@/models/advancedJobBooking';

import { computed } from 'vue';

import KSelectField from '@/components/fields/KSelectField.vue';
import { formatStopTime, timeToMinutes } from '@/helpers/stopsTimeline';

export interface Props {
  label?: string;
  disabled?: boolean;
  readonly?: boolean;
  minTime?: StopTimeObject | null;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Time',
  disabled: false,
  readonly: false,
  minTime: undefined,
});

const model = defineModel<StopTimeObject | null>({ default: null });

function toKey(value: StopTimeObject): string {
  return `${String(value.hours).padStart(2, '0')}:${String(value.minutes).padStart(2, '0')}`;
}

const minMin = computed(() =>
  props.minTime != null ? timeToMinutes(props.minTime) : -1,
);

const items = computed<Item[]>(() => {
  const min = minMin.value;
  return Array.from({ length: 48 }, (_, i) => {
    const totalMin = i * 30;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return {
      title: formatStopTime({ hours: h, minutes: m }),
      value: toKey({ hours: h, minutes: m }),
      props: { disabled: totalMin <= min },
    };
  });
});

const selectedKey = computed<string | null>(() =>
  model.value != null ? toKey(model.value) : null,
);

function handleUpdate(value: unknown): void {
  if (typeof value !== 'string') {
    model.value = null;
    return;
  }
  const [h, m] = value.split(':').map(Number);
  model.value = { hours: h, minutes: m };
}
</script>

<template>
  <KSelectField
    :model-value="selectedKey"
    :items="items"
    :label="props.label"
    :disabled="props.disabled"
    :readonly="props.readonly"
    class="timeSlotSelectField"
    data-testid="timeSlotSelectField"
    @update:model-value="handleUpdate"
  />
</template>
